import React, { useEffect, useState } from "react";
import { Calendar, Clock, MapPin, User, ExternalLink, RotateCcw, CalendarPlus } from "lucide-react";
import { Button } from "../ui/button";
import { useBooking } from "../../context/BookingContext";
import api from "../../lib/api";

const money = (cents) => (typeof cents === "number" ? `$${(cents / 100).toFixed(cents % 100 ? 2 : 0)}` : null);

const fmtWhen = (iso) => {
  try {
    return new Date(iso).toLocaleString(undefined, {
      weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

function AppointmentCard({ appt, onRebook }) {
  const primary = appt.services?.[0];
  return (
    <div className="bg-white rounded-2xl p-6 border border-slate-100" data-testid={`appt-${appt.id?.split(':').pop()}`}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="font-heading text-lg text-ink">{appt.title}</h3>
          <div className="mt-2 space-y-1.5 text-sm text-[#5C6664]">
            <div className="flex items-center gap-2"><Clock className="h-4 w-4 text-iri-mid" /> {fmtWhen(appt.start_at)}</div>
            {appt.location_name && <div className="flex items-center gap-2"><MapPin className="h-4 w-4 text-iri-mid" /> {appt.location_name}</div>}
            {primary?.staff && <div className="flex items-center gap-2"><User className="h-4 w-4 text-iri-mid" /> {primary.staff}</div>}
          </div>
        </div>
        <div className="text-right">
          {appt.cancelled ? (
            <span className="text-xs bg-rose-50 text-rose-600 px-2.5 py-1 rounded-full">Cancelled</span>
          ) : (
            <span className="text-xs bg-emerald-50 text-emerald-700 px-2.5 py-1 rounded-full">{(appt.state || "Booked").toLowerCase()}</span>
          )}
          {money(primary?.price) && <p className="text-sm text-[#5C6664] mt-2">{money(primary.price)}</p>}
        </div>
      </div>
      <div className="flex gap-2 mt-4 flex-wrap">
        <Button
          size="sm"
          onClick={() => onRebook(primary?.name || appt.title)}
          className="iri-gradient text-ink rounded-full text-xs"
          data-testid={`appt-rebook-${appt.id?.split(':').pop()}`}
        >
          <RotateCcw className="h-3.5 w-3.5 mr-1.5" /> Book again
        </Button>
        {appt.manage_url && (
          <a href={appt.manage_url} target="_blank" rel="noopener noreferrer">
            <Button size="sm" variant="outline" className="rounded-full text-xs border-ink/20 text-ink" data-testid={`appt-manage-${appt.id?.split(':').pop()}`}>
              Manage <ExternalLink className="h-3.5 w-3.5 ml-1.5" />
            </Button>
          </a>
        )}
      </div>
    </div>
  );
}

export function AppointmentsTab() {
  const { openBooking } = useBooking();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/member/appointments").then((r) => setData(r.data)).catch(() => setData({ available: false, matched: false, upcoming: [], history: [] }));
  }, []);

  if (data === null) return <p className="text-[#8A9492]" data-testid="appts-loading">Loading your appointments…</p>;

  const { available, matched, upcoming = [], history = [] } = data;

  return (
    <div data-testid="appointments-tab">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <h2 className="font-heading text-2xl font-light text-ink">My appointments</h2>
        <Button onClick={() => openBooking()} className="iri-gradient text-ink rounded-full font-medium" data-testid="appts-book-button">
          <CalendarPlus className="h-4 w-4 mr-2" /> Book new
        </Button>
      </div>

      {!available && (
        <div className="bg-white rounded-2xl p-10 border border-slate-100 text-center" data-testid="appts-unavailable">
          <Calendar className="h-8 w-8 text-[#c9c4dc] mx-auto" />
          <p className="text-[#8A9492] mt-3">Appointment history is temporarily unavailable. Please try again shortly.</p>
        </div>
      )}

      {available && !matched && (
        <div className="bg-white rounded-2xl p-10 border border-slate-100 text-center" data-testid="appts-nomatch">
          <Calendar className="h-8 w-8 text-[#c9c4dc] mx-auto" />
          <p className="text-ink font-medium mt-3">We couldn't find appointments linked to your email.</p>
          <p className="text-[#8A9492] text-sm mt-1 max-w-md mx-auto">
            If you've booked with us before under a different email, contact the spa to link your account —
            or book your first visit below.
          </p>
          <Button onClick={() => openBooking()} className="iri-gradient text-ink rounded-full mt-5" data-testid="appts-nomatch-book">
            <CalendarPlus className="h-4 w-4 mr-2" /> Book an appointment
          </Button>
        </div>
      )}

      {available && matched && (
        <div className="space-y-8">
          <section>
            <h3 className="text-xs uppercase tracking-[0.2em] text-[#8A9492] mb-3">Upcoming</h3>
            {upcoming.length === 0 ? (
              <div className="bg-white rounded-2xl p-8 border border-slate-100 text-center" data-testid="appts-upcoming-empty">
                <p className="text-[#8A9492]">No upcoming appointments. Ready for your next visit?</p>
                <Button onClick={() => openBooking()} className="iri-gradient text-ink rounded-full mt-4" data-testid="appts-upcoming-book">
                  <CalendarPlus className="h-4 w-4 mr-2" /> Book now
                </Button>
              </div>
            ) : (
              <div className="space-y-4" data-testid="appts-upcoming-list">
                {upcoming.map((a) => <AppointmentCard key={a.id} appt={a} onRebook={openBooking} />)}
              </div>
            )}
          </section>

          {history.length > 0 && (
            <section>
              <h3 className="text-xs uppercase tracking-[0.2em] text-[#8A9492] mb-3">Past visits</h3>
              <div className="space-y-4" data-testid="appts-history-list">
                {history.map((a) => <AppointmentCard key={a.id} appt={a} onRebook={openBooking} />)}
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  );
}
